import { useState, useRef } from "react";
import {
  FiSend, FiCopy, FiCheck,
  FiMessageSquare, FiSliders, FiSmile,
  FiExternalLink,
} from "react-icons/fi";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { api } from "../../services/api";
import { fetchChatbots } from "../../redux/store/chatbotSlice";

const EMOJI_ICONS = ["🤖", "💬", "🧠", "⚡", "🎯", "🛎️", "🦾", "💡", "🔮", "🌐", "🚀", "✨", "🤝", "🎓", "🛍️", "💼"];

export default function StepTwo({ updateStep, allUrls }) {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  /* ── Content ── */
  const [name,        setName]        = useState("My Chatbot");
  const [icon,        setIcon]        = useState("🤖");
  const [welcome,     setWelcome]     = useState("Hello! How can I assist you today?");
  const [placeholder, setPlaceholder] = useState("Ask me anything…");

  /* ── Appearance ── */
  const [primaryColor, setPrimaryColor] = useState("#4F46E5");

  /* ── Submit state ── */
  const [isSubmitting,     setIsSubmitting]     = useState(false);
  const [showSuccessModal, setShowSuccessModal]  = useState(false);
  const [createdBot,       setCreatedBot]        = useState(null);
  const [copied,           setCopied]            = useState(false);
  const [showIconPicker,   setShowIconPicker]    = useState(false);

  const handleSaveAndPublish = async () => {
    if (isSubmitting) return;
    if (!name.trim()) { toast.error("Chatbot name is required"); return; }

    setIsSubmitting(true);
    try {
      // 1. Create the chatbot
      const chatbot = await api.post("/chatbots", { name: name.trim(), icon });

      // 2. Save appearance
      await api.patch(`/chatbots/${chatbot.id}/appearance`, {
        primaryColor,
        bgColor: "#FFFFFF",
        botName: name.trim(),
        welcomeMessage: welcome,
        placeholder,
        theme: "light",
      });

      // 3. Queue URL sources if any were selected
      if (allUrls.length > 0) {
        await api.post(`/chatbots/${chatbot.id}/sources/links`, { urls: allUrls });
      }

      // 4. Refresh chatbot list in Redux
      dispatch(fetchChatbots());

      // 5. Celebrate 🎉
      setCreatedBot(chatbot);
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
      setTimeout(() => {
        confetti({ particleCount: 60, angle: 60,  spread: 55, origin: { x: 0 } });
        confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1 } });
      }, 250);
      setShowSuccessModal(true);
    } catch (err) {
      toast.error(err.message || "Failed to create chatbot. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(createdBot?.uniqueId || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      {/* ── Success Modal ── */}
      {showSuccessModal && createdBot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center text-4xl mx-auto mb-5">
              🎉
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Chatbot is Live!</h2>
            <p className="text-sm text-gray-500 mt-2">
              <span className="font-semibold text-gray-800">{createdBot.name}</span> has been created
              {allUrls.length > 0 && ` and ${allUrls.length} URL${allUrls.length !== 1 ? "s are" : " is"} queued for training`}.
            </p>

            {createdBot.uniqueId && (
              <div className="mt-6 bg-gray-50 border rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Chatbot ID</p>
                  <p className="text-sm font-mono text-gray-800 truncate mt-0.5">{createdBot.uniqueId}</p>
                </div>
                <button
                  onClick={handleCopyId}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition shrink-0 ${
                    copied ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowSuccessModal(false); navigate("/chatbots"); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                View All Chatbots
              </button>
              <button
                onClick={() => { setShowSuccessModal(false); navigate(`/overview/${createdBot.id}`); }}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:opacity-90 transition shadow-sm shadow-primary/30"
              >
                <FiExternalLink size={13} /> Open Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LEFT: Settings ── */}
      <div className="col-span-12 lg:col-span-7 space-y-5">

        {/* Content */}
        <SettingsCard icon={FiMessageSquare} title="Content" sub="Name, icon, welcome message, and placeholder text.">

          {/* Icon picker */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-gray-700">Chatbot Icon</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowIconPicker((v) => !v)}
                className="w-12 h-12 rounded-xl border-2 border-gray-200 text-2xl flex items-center justify-center hover:border-primary hover:bg-primary-light/30 transition"
              >
                {icon}
              </button>
              <button
                onClick={() => setShowIconPicker((v) => !v)}
                className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline"
              >
                <FiSmile size={13} /> Change icon
              </button>
            </div>
            {showIconPicker && (
              <div className="mt-2 p-3 border rounded-xl bg-gray-50 grid grid-cols-8 gap-1.5">
                {EMOJI_ICONS.map((e) => (
                  <button
                    key={e}
                    onClick={() => { setIcon(e); setShowIconPicker(false); }}
                    className={`w-9 h-9 rounded-lg text-xl flex items-center justify-center transition hover:bg-white hover:shadow-sm ${icon === e ? "bg-white shadow-sm ring-2 ring-primary" : ""}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Field label="Chatbot Name">
            <StyledInput value={name} onChange={setName} placeholder="e.g. Support Bot" />
          </Field>
          <Field label="Welcome Message" hint="First message visitors see in the chat.">
            <StyledTextarea value={welcome} onChange={setWelcome} rows={3} />
          </Field>
          <Field label="Input Placeholder" hint="Hint text shown before the user types.">
            <StyledInput value={placeholder} onChange={setPlaceholder} placeholder="Ask me anything…" />
          </Field>
        </SettingsCard>

        {/* Appearance */}
        <SettingsCard icon={FiSliders} title="Appearance" sub="Primary color for the chat widget.">
          <Field label="Primary Color">
            <ColorPicker value={primaryColor} onChange={setPrimaryColor} />
          </Field>
        </SettingsCard>

        {/* Sources summary */}
        {allUrls.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-2xl px-5 py-4">
            <p className="text-sm font-semibold text-green-800">
              ✅ {allUrls.length} URL{allUrls.length !== 1 ? "s" : ""} ready to train
            </p>
            <p className="text-xs text-green-600 mt-1">
              Training will start automatically after you publish.
            </p>
          </div>
        )}
      </div>

      {/* ── RIGHT: Live Preview ── */}
      <div className="col-span-12 lg:col-span-5">
        <div className="sticky top-4 bg-white border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-bold text-gray-900">Live Preview</p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                {allUrls.length > 0 ? `${allUrls.length} URL${allUrls.length !== 1 ? "s" : ""} selected` : "No sources yet — you can add them later"}
              </p>
            </div>
            <button
              onClick={handleSaveAndPublish}
              disabled={isSubmitting}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition shadow-sm shrink-0 ${
                isSubmitting
                  ? "bg-primary/70 text-white cursor-not-allowed"
                  : "bg-primary text-white hover:opacity-90 shadow-primary/25"
              }`}
            >
              {isSubmitting
                ? <span className="w-3.5 h-3.5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                : "🚀"}
              {isSubmitting ? "Publishing…" : "Publish"}
            </button>
          </div>

          {/* Chat widget preview */}
          <div className="rounded-2xl overflow-hidden border shadow-lg flex flex-col" style={{ height: "62vh" }}>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ backgroundColor: primaryColor }}>
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-base shrink-0">
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-white truncate">{name || "NexaGPT Assistant"}</p>
                <p className="text-[11px] text-white/75 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300 inline-block" /> Online
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50">
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-white border flex items-center justify-center text-sm shrink-0">{icon}</div>
                <div className="max-w-[80%]">
                  <div className="bg-white border rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-gray-800 shadow-sm text-sm">
                    {welcome || "Hello! How can I assist you today?"}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 ml-1">just now</p>
                </div>
              </div>
              <div className="flex items-end gap-2 justify-end">
                <div className="max-w-[80%]">
                  <div className="rounded-2xl rounded-br-sm px-3.5 py-2.5 shadow-sm text-sm text-white" style={{ backgroundColor: primaryColor }}>
                    What can you help me with?
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 text-right mr-1">just now</p>
                </div>
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-sm shrink-0">👤</div>
              </div>
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-white border flex items-center justify-center text-sm shrink-0">{icon}</div>
                <div className="max-w-[80%]">
                  <div className="bg-white border rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-gray-800 shadow-sm text-sm">
                    I'm trained on your content and ready to answer questions, capture leads, and more! 🚀
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 ml-1">just now</p>
                </div>
              </div>
            </div>

            {/* Input */}
            <div className="shrink-0 border-t bg-white px-3 py-3">
              <div className="flex items-center gap-2 border rounded-xl px-3 py-2 bg-gray-50">
                <input
                  disabled
                  placeholder={placeholder}
                  className="flex-1 text-sm bg-transparent outline-none text-gray-400 placeholder-gray-400"
                />
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor }}>
                  <FiSend size={12} className="text-white" />
                </div>
              </div>
            </div>

            <div className="shrink-0 text-center text-[10px] text-gray-400 py-1.5 bg-white border-t">
              Powered by <span className="font-semibold text-primary">NexaGPT</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function SettingsCard({ icon: Icon, title, sub, children }) {
  return (
    <div className="bg-white border rounded-2xl overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b bg-gray-50/60">
        <div className="w-8 h-8 rounded-lg bg-primary-light flex items-center justify-center">
          <Icon size={15} className="text-primary" />
        </div>
        <div>
          <p className="text-sm font-bold text-gray-900">{title}</p>
          <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
        </div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold text-gray-700">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
    </div>
  );
}

function StyledInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
    />
  );
}

function StyledTextarea({ value, onChange, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full border rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition resize-none"
    />
  );
}

function ColorPicker({ value, onChange }) {
  const inputRef = useRef(null);
  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-10 h-10 rounded-xl border-2 border-gray-200 shadow-sm shrink-0 transition hover:scale-105"
        style={{ backgroundColor: value }}
        title="Click to pick color"
      />
      <input ref={inputRef} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="sr-only" />
      <div className="flex items-center border rounded-lg px-2.5 py-2 flex-1 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition">
        <span className="text-gray-400 text-sm font-mono mr-1">#</span>
        <input
          value={value.slice(1).toUpperCase()}
          maxLength={6}
          onChange={(e) => /^[0-9A-Fa-f]{0,6}$/.test(e.target.value) && onChange("#" + e.target.value)}
          className="w-full font-mono text-sm outline-none text-gray-800"
        />
      </div>
    </div>
  );
}
